import { type ChangeEvent, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import {
  useProducts,
  useProductInventory,
  useDeleteProduct,
} from '../../hooks/useProducts'
import { useBuilds, useCompleteBuild, useDeleteBuild, useUpdateBuild } from '../../hooks/useBuilds'
import { usePrints } from '../../hooks/usePrints'
import { useSaleLineItems } from '../../hooks/useSaleLineItems'
import { useSales } from '../../hooks/useSales'
import {
  useProductPhotos,
  useUploadProductPhoto,
  useDeleteProductPhoto,
} from '../../hooks/useProductPhotos'
import {
  useProductFiles,
  useUploadProductFile,
  useDeleteProductFile,
} from '../../hooks/useProductFiles'
import { useProductHardware } from '../../hooks/useProductHardware'
import { useProductPackaging } from '../../hooks/useProductPackaging'
import { usePrinters } from '../../hooks/usePrinters'
import { useSettings } from '../../hooks/useSettings'
import { signedUrl } from '../../lib/storage'
import { computePrintCosts } from '../../lib/pricing'
import { formatMoney, formatDate } from '../../lib/format'
import type { Build, ProductFile, ProductPhoto } from '../../lib/database.types'
import { MarkSoldSheet } from '../../components/MarkSoldSheet'

export default function ProductDetail() {
  const { id } = useParams<{ id: string }>()
  const navigate = useNavigate()
  const { data: products, isLoading } = useProducts()
  const { data: lineItems } = useSaleLineItems()
  const { data: sales } = useSales()
  const inv = useProductInventory(id)
  const deleteMutation = useDeleteProduct()

  const { data: photos } = useProductPhotos(id)
  const { data: files } = useProductFiles(id)
  const { data: hardware } = useProductHardware(id)
  const { data: packaging } = useProductPackaging(id)
  const { data: printers } = usePrinters()
  const { data: settings } = useSettings()
  const { data: allBuilds } = useBuilds()
  const { data: prints } = usePrints()
  const completeBuild = useCompleteBuild()
  const updateBuild = useUpdateBuild()
  const deleteBuild = useDeleteBuild()
  const uploadPhoto = useUploadProductPhoto(id ?? '')
  const deletePhoto = useDeleteProductPhoto(id ?? '')
  const uploadFile = useUploadProductFile(id ?? '')
  const deleteFile = useDeleteProductFile(id ?? '')

  const [photoUrls, setPhotoUrls] = useState<Record<string, string>>({})
  const [marginPct, setMarginPct] = useState(60)
  const [uploadError, setUploadError] = useState('')
  const [soldBuild, setSoldBuild] = useState<Build | null>(null)

  useEffect(() => {
    if (!photos) return
    let cancelled = false
    Promise.all(
      photos.map((p) =>
        signedUrl('photos', p.file_path)
          .then((url) => ({ id: p.id, url }))
          .catch(() => ({ id: p.id, url: '' })),
      ),
    ).then((entries) => {
      if (cancelled) return
      const map: Record<string, string> = {}
      for (const e of entries) map[e.id] = e.url
      setPhotoUrls(map)
    })
    return () => {
      cancelled = true
    }
  }, [photos])

  useEffect(() => {
    if (settings?.default_margin_pct) {
      // eslint-disable-next-line react-hooks/set-state-in-effect
      setMarginPct(settings.default_margin_pct)
    }
  }, [settings?.default_margin_pct])

  const activePrinter = printers?.find((p) => p.status === 'active') ?? printers?.[0]

  const product = products?.find((p) => p.id === id)

  const pricingBreakdown = useMemo(() => {
    if (!product || !activePrinter || !settings) return null
    if (!product.typical_print_time_hours || !product.typical_filament_grams) {
      return null
    }

    const filamentCostPerGramEstimate = 0.025
    const filamentCost =
      product.typical_filament_grams *
      filamentCostPerGramEstimate *
      settings.material_efficiency_factor

    const baseCosts = computePrintCosts({
      printer: activePrinter,
      settings,
      printTimeHours: Number(product.typical_print_time_hours),
      laborMinutes: product.typical_labor_minutes ?? 0,
      usages: [],
    })

    const hardwareCost = (hardware ?? []).reduce(
      (sum, h) => sum + h.quantity * Number(h.unit_cost),
      0,
    )
    const packagingCost = (packaging ?? []).reduce(
      (sum, p) => sum + p.quantity * Number(p.unit_cost),
      0,
    )
    const postage = Number(product.default_postage_cost ?? 0)

    const landedCost =
      filamentCost +
      baseCosts.machineCost +
      baseCosts.laborCost +
      hardwareCost +
      packagingCost +
      postage

    return {
      filamentCost,
      machineCost: baseCosts.machineCost,
      laborCost: baseCosts.laborCost,
      hardwareCost,
      packagingCost,
      postage,
      landedCost,
    }
  }, [product, activePrinter, settings, hardware, packaging])

  const suggestedPrice = pricingBreakdown
    ? pricingBreakdown.landedCost / (1 - marginPct / 100)
    : null

  if (isLoading) return <div style={{ padding: 'var(--space-6)' }}>Loading…</div>
  if (!product) return <div style={{ padding: 'var(--space-6)' }}>Product not found.</div>

  const productLineItems = (lineItems ?? [])
    .filter((li) => li.product_id === product.id)
    .slice(0, 10)

  const saleById = new Map((sales ?? []).map((s) => [s.id, s]))

  // Builds for this product, sorted in_progress first
  const statusOrder: Record<string, number> = { in_progress: 0, completed: 1, abandoned: 2 }
  const productBuilds = (allBuilds ?? [])
    .filter((b) => b.product_id === product.id)
    .sort((a, b) => {
      if (statusOrder[a.status] !== statusOrder[b.status]) {
        return statusOrder[a.status] - statusOrder[b.status]
      }
      return b.started_at.localeCompare(a.started_at)
    })

  // Print count and cost per build
  const printsByBuildId = new Map<string, number>()
  const costByBuildId = new Map<string, number>()
  for (const p of prints ?? []) {
    if (p.build_id) {
      printsByBuildId.set(p.build_id, (printsByBuildId.get(p.build_id) ?? 0) + 1)
      costByBuildId.set(p.build_id, (costByBuildId.get(p.build_id) ?? 0) + Number(p.total_cost))
    }
  }

  const handleDelete = async () => {
    if (
      !window.confirm(
        `Delete product "${product.name}"? Past prints/sales will keep their references intact but the product will be gone.`,
      )
    )
      return
    await deleteMutation.mutateAsync(product.id)
    navigate('/prints/products')
  }

  const handlePhotoUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadError('')
    try {
      await uploadPhoto.mutateAsync({ file, isCover: photos?.length === 0 })
    } catch (err) {
      setUploadError((err as Error).message)
    }
    e.target.value = ''
  }

  const handleFileUpload = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    if (!file) return
    setUploadError('')
    try {
      await uploadFile.mutateAsync(file)
    } catch (err) {
      setUploadError((err as Error).message)
    }
    e.target.value = ''
  }

  const downloadFile = async (pf: ProductFile) => {
    try {
      const url = await signedUrl('model-files', pf.file_path, 600)
      const a = document.createElement('a')
      a.href = url
      a.download = pf.file_name
      a.click()
    } catch (err) {
      setUploadError((err as Error).message)
    }
  }

  const copyShareLink = async (pf: ProductFile) => {
    try {
      const url = await signedUrl('model-files', pf.file_path, 7 * 24 * 3600)
      await navigator.clipboard.writeText(url)
      setUploadError('Link copied (valid 7 days)')
      setTimeout(() => setUploadError(''), 3000)
    } catch (err) {
      setUploadError((err as Error).message)
    }
  }

  const cover = (photos ?? []).find((p: ProductPhoto) => p.is_cover) ?? photos?.[0]
  const coverUrl = cover ? photoUrls[cover.id] : null

  return (
    <div style={{ padding: 'var(--space-6)', maxWidth: 720 }}>
      {/* Hero */}
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-6)',
          textAlign: 'center',
          marginBottom: 'var(--space-4)',
        }}
      >
        {coverUrl ? (
          <img
            src={coverUrl}
            alt={product.name}
            style={{
              width: 120,
              height: 120,
              objectFit: 'cover',
              borderRadius: 'var(--radius-md)',
              marginBottom: 'var(--space-2)',
            }}
          />
        ) : (
          <div style={{ fontSize: '4rem', marginBottom: 'var(--space-2)' }}>📦</div>
        )}
        <h1 style={{ margin: 0 }}>{product.name}</h1>
        <div
          style={{
            marginTop: 'var(--space-4)',
            display: 'flex',
            gap: 'var(--space-3)',
            justifyContent: 'center',
          }}
        >
          <Link
            to={`/prints/products/${product.id}/edit`}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              background: 'var(--color-primary)',
              color: 'white',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
              fontSize: 'var(--font-size-sm)',
              textDecoration: 'none',
            }}
          >
            Edit
          </Link>
          <button
            onClick={handleDelete}
            style={{
              padding: 'var(--space-2) var(--space-4)',
              background: 'transparent',
              border: '1px solid var(--color-danger)',
              color: 'var(--color-danger)',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
              fontSize: 'var(--font-size-sm)',
              cursor: 'pointer',
            }}
          >
            Delete
          </button>
        </div>
      </div>

      {/* Quick stats */}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(3, 1fr)',
          gap: 'var(--space-3)',
          marginBottom: 'var(--space-4)',
        }}
      >
        <Stat label="In stock" value={String(inv.inStock)} />
        <Stat label="Sold" value={String(inv.unitsSold)} />
        <Stat label="Avg margin" value={inv.unitsSold > 0 ? formatMoney(inv.avgMargin) : '—'} />
      </div>

      {/* Details */}
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)',
          marginBottom: 'var(--space-4)',
        }}
      >
        <h3 style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)' }}>
          Details
        </h3>
        <Row label="Source" value={product.model_source ?? '—'} />
        <Row label="License" value={product.license ?? '—'} />
        <Row
          label="Default price"
          value={
            product.default_unit_price != null
              ? formatMoney(Number(product.default_unit_price))
              : '—'
          }
        />
        <Row
          label="Avg cost per unit"
          value={inv.unitsProduced > 0 ? formatMoney(inv.avgUnitCost) : '—'}
        />
        <Row
          label="Typical print time"
          value={
            product.typical_print_time_hours != null
              ? `${product.typical_print_time_hours}h`
              : '—'
          }
        />
        <Row
          label="Typical filament"
          value={
            product.typical_filament_grams != null
              ? `${product.typical_filament_grams}g`
              : '—'
          }
        />
        {product.notes && (
          <div
            style={{
              marginTop: 'var(--space-3)',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--color-text-muted)',
            }}
          >
            {product.notes}
          </div>
        )}
      </div>

      {/* Builds */}
      <div style={{ background: 'var(--color-surface)', borderRadius: 'var(--radius-lg)', padding: 'var(--space-4)', marginBottom: 'var(--space-4)' }}>
        <h3 style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)' }}>🧩 Builds</h3>
        {productBuilds.length === 0 ? (
          <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--font-size-sm)' }}>
            No builds yet. Builds are created automatically when you log a print of this product.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {productBuilds.map((b) => {
              const printCount = printsByBuildId.get(b.id) ?? 0
              const cost = costByBuildId.get(b.id) ?? 0
              const statusIcon = b.status === 'completed' ? '✓' : b.status === 'abandoned' ? '✗' : '⏳'
              const statusColor =
                b.status === 'completed' ? 'var(--color-success)'
                : b.status === 'abandoned' ? 'var(--color-text-faint)'
                : 'var(--color-warning)'
              return (
                <li key={b.id} style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-2) 0',
                  borderBottom: '1px solid var(--color-border)',
                  fontSize: 'var(--font-size-sm)',
                }}>
                  <span style={{ color: statusColor, fontWeight: 700, fontSize: 'var(--font-size-base)' }}>
                    {statusIcon}
                  </span>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ fontWeight: 600 }}>{b.name ?? `Build ${b.id.slice(0, 6)}`}</div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                      {printCount} print{printCount === 1 ? '' : 's'} · {formatMoney(cost)}
                      {b.status === 'completed' && ` · ${b.units} unit${b.units === 1 ? '' : 's'}`}
                    </div>
                  </div>
                  {b.status === 'in_progress' && (
                    <>
                      <button
                        type="button"
                        onClick={() => completeBuild.mutate(b.id)}
                        style={{
                          padding: 'var(--space-1) var(--space-3)',
                          background: 'var(--color-primary)',
                          color: 'white',
                          borderRadius: 'var(--radius-md)',
                          fontWeight: 600,
                          fontSize: 'var(--font-size-xs)',
                          border: 'none',
                          cursor: 'pointer',
                        }}
                        title="Mark this build complete (adds to inventory)"
                      >
                        Complete
                      </button>
                      <button
                        type="button"
                        onClick={() => {
                          if (window.confirm('Abandon this build? Print costs still tracked but no inventory added.'))
                            updateBuild.mutate({ id: b.id, status: 'abandoned' })
                        }}
                        style={{ color: 'var(--color-text-faint)', fontSize: 'var(--font-size-xs)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                        title="Abandon"
                      >
                        ✗
                      </button>
                    </>
                  )}
                  {b.status === 'completed' && inv.inStock > 0 && (
                    <button
                      type="button"
                      onClick={() => setSoldBuild(b)}
                      style={{
                        padding: 'var(--space-1) var(--space-3)',
                        background: 'var(--color-success)',
                        color: 'white',
                        borderRadius: 'var(--radius-md)',
                        fontWeight: 600,
                        fontSize: 'var(--font-size-xs)',
                        border: 'none',
                        cursor: 'pointer',
                        whiteSpace: 'nowrap',
                      }}
                      title="Record a sale for this build"
                    >
                      💰 Mark sold
                    </button>
                  )}
                  <button
                    type="button"
                    onClick={async () => {
                      if (!window.confirm('Delete this build? Linked prints will be unlinked but kept.')) return
                      try {
                        await deleteBuild.mutateAsync(b.id)
                      } catch (err) {
                        window.alert((err as Error).message)
                      }
                    }}
                    style={{ color: 'var(--color-danger)', fontSize: 'var(--font-size-xs)', background: 'transparent', border: 'none', cursor: 'pointer' }}
                    title="Delete"
                  >
                    Delete
                  </button>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* Recent sales */}
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)',
        }}
      >
        <h3 style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)' }}>
          Recent sales
        </h3>
        {productLineItems.length === 0 ? (
          <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--font-size-sm)' }}>
            No sales of this product yet.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
            {productLineItems.map((li) => {
              const sale = saleById.get(li.sale_id)
              return (
                <li
                  key={li.id}
                  style={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    padding: 'var(--space-2) 0',
                    borderBottom: '1px solid var(--color-border)',
                    fontSize: 'var(--font-size-sm)',
                  }}
                >
                  <span>
                    {sale ? formatDate(sale.sale_date) : '?'} &middot;{' '}
                    {li.quantity}&times; @ {formatMoney(Number(li.unit_price))}
                  </span>
                  <span style={{ fontWeight: 600 }}>
                    {formatMoney(li.quantity * Number(li.unit_price))}
                  </span>
                </li>
              )
            })}
          </ul>
        )}
      </div>

      {/* Photo gallery */}
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)',
          marginTop: 'var(--space-4)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-3)',
          }}
        >
          <h3 style={{ fontSize: 'var(--font-size-sm)' }}>📷 Photos</h3>
          <label
            style={{
              padding: 'var(--space-2) var(--space-3)',
              background: 'var(--color-primary)',
              color: 'white',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
              fontSize: 'var(--font-size-xs)',
              cursor: 'pointer',
            }}
          >
            {uploadPhoto.isPending ? 'Uploading…' : '+ Add photo'}
            <input
              type="file"
              accept="image/*"
              onChange={handlePhotoUpload}
              disabled={uploadPhoto.isPending}
              style={{ display: 'none' }}
            />
          </label>
        </div>
        {!photos || photos.length === 0 ? (
          <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--font-size-sm)' }}>
            No photos yet.
          </p>
        ) : (
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))',
              gap: 'var(--space-2)',
            }}
          >
            {photos.map((p: ProductPhoto) => {
              const url = photoUrls[p.id]
              return (
                <div key={p.id} style={{ position: 'relative' }}>
                  {url ? (
                    <img
                      src={url}
                      alt={p.caption ?? ''}
                      style={{
                        width: '100%',
                        aspectRatio: '1/1',
                        objectFit: 'cover',
                        borderRadius: 'var(--radius-md)',
                      }}
                    />
                  ) : (
                    <div
                      style={{
                        width: '100%',
                        aspectRatio: '1/1',
                        background: 'var(--color-surface-2)',
                        borderRadius: 'var(--radius-md)',
                      }}
                    />
                  )}
                  <button
                    onClick={() => {
                      if (window.confirm('Delete this photo?')) deletePhoto.mutate(p)
                    }}
                    style={{
                      position: 'absolute',
                      top: 4,
                      right: 4,
                      background: 'rgba(0,0,0,0.6)',
                      color: 'white',
                      borderRadius: '50%',
                      width: 24,
                      height: 24,
                      fontSize: 14,
                      fontWeight: 700,
                      border: 'none',
                      cursor: 'pointer',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      lineHeight: 1,
                    }}
                    title="Delete"
                  >
                    ×
                  </button>
                  {p.is_cover && (
                    <div
                      style={{
                        position: 'absolute',
                        bottom: 4,
                        left: 4,
                        background: 'var(--color-primary)',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: 'var(--radius-sm)',
                        fontSize: 'var(--font-size-xs)',
                        fontWeight: 600,
                      }}
                    >
                      COVER
                    </div>
                  )}
                </div>
              )
            })}
          </div>
        )}
      </div>

      {/* Model files */}
      <div
        style={{
          background: 'var(--color-surface)',
          borderRadius: 'var(--radius-lg)',
          padding: 'var(--space-4)',
          marginTop: 'var(--space-4)',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 'var(--space-3)',
          }}
        >
          <h3 style={{ fontSize: 'var(--font-size-sm)' }}>📎 Model files</h3>
          <label
            style={{
              padding: 'var(--space-2) var(--space-3)',
              background: 'var(--color-primary)',
              color: 'white',
              borderRadius: 'var(--radius-md)',
              fontWeight: 600,
              fontSize: 'var(--font-size-xs)',
              cursor: 'pointer',
            }}
          >
            {uploadFile.isPending ? 'Uploading…' : '+ Upload file'}
            <input
              type="file"
              accept=".stl,.3mf,.gcode,.obj"
              onChange={handleFileUpload}
              disabled={uploadFile.isPending}
              style={{ display: 'none' }}
            />
          </label>
        </div>
        {!files || files.length === 0 ? (
          <p style={{ color: 'var(--color-text-faint)', fontSize: 'var(--font-size-sm)' }}>
            No model files yet.
          </p>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
            {files.map((pf: ProductFile) => (
              <li
                key={pf.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 'var(--space-3)',
                  padding: 'var(--space-2) 0',
                  borderBottom: '1px solid var(--color-border)',
                }}
              >
                <div
                  style={{
                    width: 36,
                    height: 36,
                    borderRadius: 'var(--radius-sm)',
                    background: 'var(--color-primary-soft)',
                    color: 'var(--color-primary-text)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    fontWeight: 700,
                    fontSize: 'var(--font-size-xs)',
                    textTransform: 'uppercase',
                    flexShrink: 0,
                  }}
                >
                  {pf.file_type}
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div
                    style={{
                      fontWeight: 600,
                      fontSize: 'var(--font-size-sm)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {pf.file_name}
                  </div>
                  <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--color-text-faint)' }}>
                    {(pf.size_bytes / 1024 / 1024).toFixed(1)} MB
                  </div>
                </div>
                <button
                  onClick={() => downloadFile(pf)}
                  title="Download"
                  style={{
                    color: 'var(--color-primary)',
                    fontWeight: 600,
                    padding: 'var(--space-1) var(--space-2)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-base)',
                  }}
                >
                  ⬇
                </button>
                <button
                  onClick={() => copyShareLink(pf)}
                  title="Share link"
                  style={{
                    color: 'var(--color-primary)',
                    fontWeight: 600,
                    padding: 'var(--space-1) var(--space-2)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-base)',
                  }}
                >
                  🔗
                </button>
                <button
                  onClick={() => {
                    if (window.confirm('Delete this file?')) deleteFile.mutate(pf)
                  }}
                  title="Delete"
                  style={{
                    color: 'var(--color-danger)',
                    fontWeight: 600,
                    padding: 'var(--space-1) var(--space-2)',
                    background: 'transparent',
                    border: 'none',
                    cursor: 'pointer',
                    fontSize: 'var(--font-size-base)',
                  }}
                >
                  ×
                </button>
              </li>
            ))}
          </ul>
        )}
        {uploadError && (
          <p
            style={{
              color: uploadError.includes('copied')
                ? 'var(--color-success)'
                : 'var(--color-danger)',
              fontSize: 'var(--font-size-sm)',
              marginTop: 'var(--space-2)',
            }}
          >
            {uploadError}
          </p>
        )}
      </div>

      {/* Mark sold sheet */}
      {soldBuild && (
        <MarkSoldSheet
          open={soldBuild != null}
          onClose={() => setSoldBuild(null)}
          build={soldBuild}
          product={product}
          costBasis={pricingBreakdown?.landedCost}
        />
      )}

      {/* PFA Pricing calculator */}
      {pricingBreakdown && suggestedPrice !== null ? (
        <div
          style={{
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-4)',
            marginTop: 'var(--space-4)',
          }}
        >
          <h3 style={{ fontSize: 'var(--font-size-sm)', marginBottom: 'var(--space-3)' }}>
            💰 Pricing calculator (PFA)
          </h3>

          {/* Hero price card */}
          <div
            style={{
              background: 'linear-gradient(135deg, #6366f1, #8b5cf6)',
              color: 'white',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--space-4)',
              marginBottom: 'var(--space-3)',
            }}
          >
            <div
              style={{
                fontSize: 'var(--font-size-xs)',
                opacity: 0.85,
                textTransform: 'uppercase',
                letterSpacing: '0.5px',
              }}
            >
              Suggested price @ {marginPct}% margin
            </div>
            <div style={{ fontSize: '2rem', fontWeight: 700, marginTop: 'var(--space-1)' }}>
              {formatMoney(suggestedPrice)}
            </div>
            <div style={{ fontSize: 'var(--font-size-xs)', opacity: 0.9, marginTop: 'var(--space-1)' }}>
              Cost basis: {formatMoney(pricingBreakdown.landedCost)} · profit:{' '}
              {formatMoney(suggestedPrice - pricingBreakdown.landedCost)}
            </div>
          </div>

          {/* Margin slider */}
          <div style={{ marginBottom: 'var(--space-3)' }}>
            <label
              style={{
                display: 'block',
                fontSize: 'var(--font-size-sm)',
                fontWeight: 600,
                marginBottom: 'var(--space-2)',
              }}
            >
              Margin %
            </label>
            <input
              type="range"
              min="20"
              max="80"
              step="5"
              value={marginPct}
              onChange={(e) => setMarginPct(Number(e.target.value))}
              style={{ width: '100%' }}
            />
            <div
              style={{
                display: 'flex',
                justifyContent: 'space-between',
                fontSize: 'var(--font-size-xs)',
                color: 'var(--color-text-faint)',
              }}
            >
              <span>20%</span>
              <span>50%</span>
              <span>80%</span>
            </div>
          </div>

          {/* 3 preset tiers */}
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(3, 1fr)',
              gap: 'var(--space-2)',
              marginBottom: 'var(--space-3)',
            }}
          >
            {[50, 60, 70].map((pct) => {
              const price = pricingBreakdown.landedCost / (1 - pct / 100)
              const isSelected = pct === marginPct
              return (
                <button
                  key={pct}
                  type="button"
                  onClick={() => setMarginPct(pct)}
                  style={{
                    padding: 'var(--space-3)',
                    background: isSelected ? 'var(--color-primary-soft)' : 'var(--color-surface-2)',
                    border: isSelected ? '2px solid var(--color-primary)' : '2px solid transparent',
                    borderRadius: 'var(--radius-md)',
                    textAlign: 'center',
                    cursor: 'pointer',
                  }}
                >
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--color-text-muted)',
                      fontWeight: 600,
                    }}
                  >
                    {pct}%
                  </div>
                  <div
                    style={{
                      fontSize: 'var(--font-size-base)',
                      fontWeight: 700,
                      marginTop: 'var(--space-1)',
                    }}
                  >
                    {formatMoney(price)}
                  </div>
                </button>
              )
            })}
          </div>

          {/* Cost breakdown (collapsible) */}
          <details style={{ fontSize: 'var(--font-size-sm)' }}>
            <summary
              style={{
                cursor: 'pointer',
                color: 'var(--color-text-muted)',
                fontWeight: 600,
                marginBottom: 'var(--space-2)',
              }}
            >
              Cost breakdown
            </summary>
            <div style={{ marginLeft: 'var(--space-4)' }}>
              <CostRow label="Filament" value={pricingBreakdown.filamentCost} />
              <CostRow
                label="Machine (capital + electric + buffer)"
                value={pricingBreakdown.machineCost}
              />
              <CostRow label="Labor" value={pricingBreakdown.laborCost} />
              <CostRow label="Hardware" value={pricingBreakdown.hardwareCost} />
              <CostRow label="Packaging" value={pricingBreakdown.packagingCost} />
              <CostRow label="Postage" value={pricingBreakdown.postage} />
              <div
                style={{
                  borderTop: '1px solid var(--color-border)',
                  marginTop: 'var(--space-1)',
                  paddingTop: 'var(--space-1)',
                }}
              >
                <CostRow label="Total landed cost" value={pricingBreakdown.landedCost} bold />
              </div>
            </div>
          </details>
        </div>
      ) : (
        <div
          style={{
            background: 'var(--color-surface)',
            borderRadius: 'var(--radius-lg)',
            padding: 'var(--space-4)',
            marginTop: 'var(--space-4)',
            color: 'var(--color-text-muted)',
            fontSize: 'var(--font-size-sm)',
          }}
        >
          💰 Add typical print time + filament grams to this product (edit it) to enable the
          pricing calculator.
        </div>
      )}
    </div>
  )
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        background: 'var(--color-surface)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--space-4)',
        textAlign: 'center',
      }}
    >
      <div
        style={{
          fontSize: 'var(--font-size-xl)',
          fontWeight: 700,
          color: 'var(--color-primary)',
        }}
      >
        {value}
      </div>
      <div
        style={{
          fontSize: 'var(--font-size-xs)',
          color: 'var(--color-text-faint)',
          textTransform: 'uppercase',
          letterSpacing: '0.3px',
          fontWeight: 600,
          marginTop: 'var(--space-1)',
        }}
      >
        {label}
      </div>
    </div>
  )
}

function Row({ label, value }: { label: string; value: string }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: 'var(--space-2) 0',
        fontSize: 'var(--font-size-sm)',
      }}
    >
      <span style={{ color: 'var(--color-text-muted)' }}>{label}</span>
      <span style={{ fontWeight: 600 }}>{value}</span>
    </div>
  )
}

function CostRow({ label, value, bold }: { label: string; value: number; bold?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: 'var(--space-1) 0',
        fontWeight: bold ? 700 : 400,
      }}
    >
      <span>{label}</span>
      <span>{formatMoney(value)}</span>
    </div>
  )
}
